import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="form"
export default class extends Controller {
  static targets = ["checkbox", "info", "decimalField", "integerField"];

  get formFields() {
    return Array.from(this.element.elements);
  }

  get firstInvalidField() {
    return this.formFields.find((field) => !field.checkValidity());
  }

  connect() {
    this.element.setAttribute("novalidate", true);
    this.element.addEventListener("submit", this.onSubmit);
    //this.element.addEventListener('blur', this.onSubmit)
    this.element.addEventListener("ajax:beforeSend", this.onSubmit);

    // Format controls for zip, phone, card exp, and security code
    this.zipCodeField = this.element.querySelector('[data-zip-code-target="true"]');
    if (this.zipCodeField) {
      this.zipCodeField.addEventListener("input", this.handleZipCodeInput);
    }
    this.phoneNumberField = this.element.querySelector('[data-phone-number-target="true"]');
    if (this.phoneNumberField) {
      this.phoneNumberField.addEventListener("input", this.handlePhoneNumberInput);
    }
    this.cardNumberField = this.element.querySelector('[data-card-number-target="true"]');
    if (this.cardNumberField) {
      this.cardNumberField.addEventListener("input", this.handleCardNumberInput);
    }
    this.cardExpField = this.element.querySelector('[data-card-exp-target="true"]');
    if (this.cardExpField) {
      this.cardExpField.addEventListener("input", this.handleCardExpInput);
    }
    this.securityCodeField = this.element.querySelector('[data-card-cvv-target="true"]');
    if (this.securityCodeField) {
      this.securityCodeField.addEventListener("input", this.handleSecurityCodeInput);
    }
    this.keyField = this.element.querySelector('[data-key-target="true"');
    if (this.keyField) {
      this.keyField.addEventListener("input", this.handleKeyInput);
    }
    this.keyBasisField = this.element.querySelector('[data-key-basis-target="true"');
    if (this.keyBasisField) {
      this.keyBasisField.addEventListener("input", this.handleKeyBasisInput);
    }
    this.integerFieldTargets.forEach((field) => {
      field.addEventListener("input", this.handleIntegerInput.bind(this));
    });
    this.decimalFieldTargets.forEach((field) => {
      field.addEventListener("input", this.handleDecimalInput.bind(this));
    });
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onSubmit);
    //this.element.removeEventListener('blur', this.onSubmit)
    this.element.removeEventListener("ajax:beforeSend", this.onSubmit);
    if (this.zipCodeField) {
      this.zipCodeField.removeEventListener("input", this.handleZipCodeInput);
    }
    if (this.phoneNumberField) {
      this.phoneNumberField.removeEventListener("input", this.handlePhoneNumberInput);
    }
    if (this.cardNumberField) {
      this.cardNumberField.removeEventListener("input", this.handleCardNumberInput);
    }
    if (this.cardExpField) {
      this.cardExpField.removeEventListener("input", this.handleCardExpInput);
    }
    if (this.securityCodeField) {
      this.securityCodeField.removeEventListener("input", this.handleSecurityCodeInput);
    }
    if (this.keyField) {
      this.keyField.removeEventListener("input", this.handleKeyInput);
    }
    if (this.keyBasisField) {
      this.keyBasisField.removeEventListener("input", this.handleKeyBasisInput);
    }
  }

  handleZipCodeInput = (event) => {
    let value = event.target.value;
    value = this.processZipCode(value);
    event.target.value = value;
  };

  processZipCode(zipCode) {
    // Remove non-numeric characters
    let processed = zipCode.replace(/\D/g, "");

    // Limit length to 9 digits
    processed = processed.substring(0, 9);

    // Insert hyphen after 5th digit if length > 5
    if (processed.length > 5) {
      processed = processed.slice(0, 5) + "-" + processed.slice(5);
    }

    return processed;
  }

  handlePhoneNumberInput = (event) => {
    let value = event.target.value;
    value = this.processPhoneNumber(value);
    event.target.value = value;
  };

  processPhoneNumber(phoneNumber) {
    // Remove non-numeric characters
    let processed = phoneNumber.replace(/\D/g, "");

    // Limit length to 11 digits
    processed = processed.substring(0, 11);

    // Format based on length
    if (processed.length > 10) {
      // Format: x-xxx-xxx-xxxx
      processed = processed.replace(/^(\d)(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3-$4");
    } else if (processed.length > 6) {
      // Format: xxx-xxx-xxxx
      processed = processed.replace(/^(\d{3})(\d{3})(\d{1,4})$/, "$1-$2-$3");
    } else if (processed.length > 3) {
      // Format: xxx-xxx
      processed = processed.replace(/^(\d{3})(\d{1,3})$/, "$1-$2");
    }

    return processed;
  }

  handleCardNumberInput = (event) => {
    let value = event.target.value;
    value = this.processCardNumber(value);
    event.target.value = value;
  };

  processCardNumber(cardNumber) {
    // Remove non-numeric characters
    let processed = cardNumber.replace(/\D/g, "");

    // Limit length to 19 digits
    processed = processed.substring(0, 19);

    // Insert space after every 4 digits
    processed = processed.replace(/(\d{4})(?=\d)/g, "$1 ");

    return processed;
  }

  handleCardExpInput = (event) => {
    let value = event.target.value;
    value = this.processCardExp(value);
    event.target.value = value;
  };

  processCardExp(cardExp) {
    // Remove non-numeric characters
    let processed = cardExp.replace(/\D/g, "");

    // Limit length to 4 digits
    processed = processed.substring(0, 4);

    // Ensure the first two digits are valid
    if (processed.length > 2 && parseInt(processed.slice(0, 2)) > 12) {
      processed = "12" + processed.slice(2);
    }

    // Insert slash after 2nd digit if length > 2
    if (processed.length > 2) {
      processed = processed.slice(0, 2) + "/" + processed.slice(2);
    }

    return processed;
  }

  handleSecurityCodeInput = (event) => {
    let value = event.target.value;
    value = this.processSecurityCode(value);
    event.target.value = value;
  };

  handleKeyInput = (event) => {
    let value = event.target.value;
    value = this.processKey(value);
    event.target.value = value;
  };

  handleKeyBasisInput = (event) => {
    let value = event.target.value;
    value = this.processKey(value);
    this.keyField.value = value;
  };

  handleIntegerInput(event) {
    let value = event.target.value;
    value = this.processInteger(value);
    event.target.value = value;
  }

  handleDecimalInput(event) {
    let value = event.target.value;
    value = this.processDecimal(value);
    event.target.value = value;
  }

  processKey(key) {
    return key
      .replace(/\s+/g, "_")
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "");
  }

  processSecurityCode(SecurityCode) {
    // Remove non-numeric characters
    let processed = SecurityCode.replace(/\D/g, "");

    // Limit length to 4 digits (American Express can have 4 digits)
    processed = processed.substring(0, 4);

    return processed;
  }

  processInteger(value) {
    // Remove non-digit characters
    let processed = value.replace(/[^\d]/g, "");

    // Remove leading zeros, but allow "0"
    processed = processed.replace(/^0+(?=\d)/, "");

    return processed;
  }

  processDecimal(value) {
    // Remove non-digit and non-dot characters
    let processed = value.replace(/[^\d.]/g, "");

    // Only allow the first dot
    const parts = processed.split(".");
    if (parts.length > 2) {
      processed = parts[0] + "." + parts.slice(1).join("");
    }

    // Remove leading zeros, but allow "0" or just one "0" before a decimal point
    processed = processed.replace(/^0+(?=\d)/, "");

    return processed;
  }

  onBlur = (event) => {
    this.validateField(event.target);
  };

  onSubmit = (event) => {
    if (this.validateForm()) return;

    event.preventDefault();
    this.firstInvalidField.focus();
  };

  validateForm() {
    let isValid = true;

    this.formFields.forEach((field) => {
      if (this.shouldValidateField(field) && !this.validateField(field)) isValid = false;
    });

    return isValid;
  }

  validateField(field) {
    if (!this.shouldValidateField(field)) return true;

    const isValid = field.checkValidity();
    field.classList.toggle("invalid", !isValid);
    this.refreshErrorForInvalidField(field, isValid);
    return isValid;
  }

  shouldValidateField(field) {
    if (["file", "reset", "submit", "button"].includes(field.type)) return false;

    return !field.disabled;
  }

  refreshErrorForInvalidField(field, isValid) {
    this.removeExistingErrorMessage(field);
    if (!isValid) this.showErrorForInvalidField(field);
  }

  removeExistingErrorMessage(field) {
    const fieldContainer = field.parentElement;
    if (!fieldContainer) return;

    const existingErrorMessageElement = fieldContainer.querySelector(".error");
    if (existingErrorMessageElement) {
      existingErrorMessageElement.parentNode.removeChild(existingErrorMessageElement);
    }
  }

  showErrorForInvalidField(field, errorMessage = null) {
    field.insertAdjacentHTML("afterend", this.buildFieldErrorHtml(field, errorMessage));
  }

  buildFieldErrorHtml(field, errorMessage = null) {
    return `<p class="error">${errorMessage || field.validationMessage}</p>`;
  }

  toggleText() {
    const checkedText = this.infoTarget.dataset.checkedText;
    const uncheckedText = this.infoTarget.dataset.uncheckedText;
    this.infoTarget.textContent = this.checkboxTarget.checked ? checkedText : uncheckedText;
  }
}
